import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import HTMLFlipBook from 'react-pageflip';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCamera,
  FaCheck,
  FaCloudUploadAlt,
  FaDownload,
  FaImages,
  FaLock,
  FaPlay,
  FaQrcode,
  FaSignOutAlt,
  FaTimes,
  FaUpload,
} from 'react-icons/fa';
import { getApiUrl, getFetchErrorMessage } from '../../utils/api.js';
import '../../styles/EPhotoAlbum.css';

import mmIcon from '../../assets/images/merizhelle-kate-marvic/meri-mm-icon.webp';
import introDesktop from '../../assets/images/merizhelle-kate-marvic/merizhelle-marvic-intro-embroidered-garden-desktop-v8.webp';
import introMobile from '../../assets/images/merizhelle-kate-marvic/merizhelle-marvic-intro-embroidered-garden-mobile-v8.webp';
import bookPageBg from '../../assets/images/merizhelle-kate-marvic/merizhelle-marvic-book-page-bg.webp';
import paperTexture from '../../assets/images/merizhelle-kate-marvic/paper-ivory-texture.svg';

const DEFAULT_ALBUM_ID = 'meri';
const CLEAN_ALBUM_URL = 'https://e-invitation.online/e-photo-album/meri';
const GALLERY_CODE_STORAGE_KEY = 'meri_photo_album_gallery_code';

function normalizeAlbumId(value) {
  return String(value || DEFAULT_ALBUM_ID)
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || DEFAULT_ALBUM_ID;
}

function getEmbedUrl(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value) return '';

  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      const id = url.pathname.replace('/', '');
      return id ? getYouTubeEmbedUrl(id) : '';
    }

    if (host.endsWith('youtube.com')) {
      const id = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
      return id ? getYouTubeEmbedUrl(id) : '';
    }

    if (host === 'drive.google.com') {
      const match = url.pathname.match(/\/file\/d\/([^/]+)/);
      return match?.[1] ? `https://drive.google.com/file/d/${match[1]}/preview` : '';
    }
  } catch {
    return '';
  }

  return '';
}

function getYouTubeEmbedUrl(id) {
  const url = new URL(`https://www.youtube.com/embed/${id}`);
  url.searchParams.set('fs', '0');
  url.searchParams.set('modestbranding', '1');
  url.searchParams.set('playsinline', '1');
  url.searchParams.set('rel', '0');
  return url.toString();
}

function getVideoItems() {
  return String(import.meta.env.VITE_MERI_PRENUP_VIDEO_URLS || '')
    .split(',')
    .map((src) => src.trim())
    .filter(Boolean)
    .map((src) => ({
      title: 'Prenup Video (Meri & Marvic)',
      src,
      embedUrl: getEmbedUrl(src),
    }));
}

function formatDate(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

// run — so calling stopPropagation() from a React event handler is too late
// change the identity of the top-level page array passed to HTMLFlipBook —

const BookPage = forwardRef(function BookPage({ children }, ref) {
  return (
    // react-pageflip's default "soft" density splits each page into two
    // clip-path polygons to fake a paper bend. That math is tuned for
    // continuous book artwork — with our fully custom, differently-laid-out
    // screens per page it desyncs and both pages render fully visible,
    // overlapping, mid-flip. "hard" density skips the clip-path split
    // entirely and does a single rigid rotation, which has no such failure
    // mode.
    <div className="epa-book-page" ref={ref} data-density="hard">
      {children}
    </div>
  );
});

function useSuppressBookGesture() {
  const ref = useRef(null);
  const touchStartRef = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // Only the events react-pageflip's own drag/gesture detection actually
    // listens for (mousedown/touchstart on the book, mouseup/touchend on
    // window) need to be stopped here. Deliberately NOT stopping 'click':
    // the library never listens for it, and React 17+'s onClick is
    // delegated to the root container, so stopping propagation on 'click'
    // at the target would prevent it from ever bubbling up to React at
    // all — silently breaking this button's own onClick.
    const stopBookEvent = (event) => {
      if (event.type === 'touchstart') {
        const touch = event.changedTouches?.[0];
        touchStartRef.current = touch
          ? { x: touch.clientX, y: touch.clientY, target: event.target }
          : null;
      }

      if (event.type === 'touchend') {
        const touch = event.changedTouches?.[0];
        const started = touchStartRef.current;
        const startTargetButton = started?.target?.closest?.('.epa-photo-tile-open');
        const endTargetButton = event.target?.closest?.('.epa-photo-tile-open');
        const openButton = endTargetButton || startTargetButton;

        if (touch && started && openButton) {
          const movedX = Math.abs(touch.clientX - started.x);
          const movedY = Math.abs(touch.clientY - started.y);

          if (movedX <= 10 && movedY <= 10) {
            if (event.cancelable) event.preventDefault();
            window.setTimeout(() => openButton.click(), 0);
          }
        }

        touchStartRef.current = null;
      }

      event.stopPropagation();
    };
    const events = [
      'mousedown',
      'mousemove',
      'mouseup',
      'touchstart',
      'touchmove',
      'touchend',
    ];

    events.forEach((eventName) => {
      node.addEventListener(eventName, stopBookEvent, { capture: true });
    });

    return () => {
      events.forEach((eventName) => {
        node.removeEventListener(eventName, stopBookEvent, { capture: true });
      });
    };
  }, []);

  return ref;
}

function stopInteractiveEvent(event) {
  event.stopPropagation();
}

function IntroPage({ albumUrl, onOpenVideo }) {
  const logoButtonRef = useSuppressBookGesture();

  return (
    <section className="epa-panel epa-panel-intro" aria-label="Meri photo album welcome">
      <p className="epa-intro-eyebrow">Welcome to our</p>

      <div className="epa-intro-mark">
        <button
          ref={logoButtonRef}
          className="epa-logo-button"
          type="button"
          onClick={onOpenVideo}
          aria-label="Open prenup videos"
        >
          <img src={mmIcon} alt="Meri and Marvic monogram" />
          <span>Meri &amp; Marvic</span>
          <em>Wedding E-Photo Album</em>
        </button>
      </div>

      <p className="epa-intro-copy">
        Tap the monogram to watch our prenup films, upload your favorite shots from the day,
        or share this album with someone who was there.
      </p>

      <div className="epa-qr-wrap" aria-label="Photo album QR code">
        <QRCodeSVG
          value={albumUrl}
          size={132}
          bgColor="#fffaf0"
          fgColor="#22321e"
          level="M"
          marginSize={1}
        />
        <p><FaQrcode aria-hidden="true" /> Scan to open on your phone</p>
      </div>
    </section>
  );
}

function VideoPage({ videoItems, onUploadClick }) {
  const uploadButtonRef = useSuppressBookGesture();
  const videoStackRef = useSuppressBookGesture();

  return (
    <section className="epa-panel epa-panel-video" aria-label="Prenup videos">
      <div className="epa-panel-head">
        <h1>Prenup Videos</h1>
      </div>

      <div className="epa-video-stack" ref={videoStackRef}>
        {videoItems.length > 0 ? (
          videoItems.map((video) => (
            <article className="epa-video-frame" key={video.src}>
              <div className="epa-video-frame-media">
                {video.embedUrl ? (
                  <iframe
                    src={video.embedUrl}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                  />
                ) : (
                  <video
                    controls
                    controlsList="nodownload nofullscreen noremoteplayback"
                    disablePictureInPicture
                    disableRemotePlayback
                    preload="metadata"
                  >
                    <source src={video.src} />
                  </video>
                )}
              </div>
              <p className="epa-video-frame-title">
                <FaPlay aria-hidden="true" />
                {video.title}
              </p>
            </article>
          ))
        ) : (
          <article className="epa-video-frame epa-video-frame-empty">
            <FaPlay aria-hidden="true" />
            <p>Prenup video coming soon</p>
          </article>
        )}
      </div>

      <button
        ref={uploadButtonRef}
        className="epa-primary-action"
        type="button"
        onClick={onUploadClick}
      >
        <FaUpload aria-hidden="true" />
        Upload Photos
      </button>
    </section>
  );
}

function UploadPage({ albumId }) {
  const fileInputRef = useRef(null);
  const formRef = useSuppressBookGesture();

  const [guestName, setGuestName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState({ status: 'idle', message: '' });

  useEffect(() => {
    const previews = selectedFiles.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file),
    }));
    setFilePreviews(previews);

    return () => previews.forEach((preview) => URL.revokeObjectURL(preview.url));
  }, [selectedFiles]);

  function addFiles(fileList) {
    const incoming = Array.from(fileList || []).filter((file) => file.type.startsWith('image/'));
    if (incoming.length === 0) return;

    setSelectedFiles((current) => {
      const seen = new Set(current.map((file) => `${file.name}_${file.size}_${file.lastModified}`));
      const merged = [...current];
      incoming.forEach((file) => {
        const key = `${file.name}_${file.size}_${file.lastModified}`;
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(file);
        }
      });
      return merged;
    });
    setUploadState({ status: 'idle', message: '' });
  }

  function handleFileChange(event) {
    addFiles(event.target.files);
    event.target.value = '';
  }

  function handleDrop(event) {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer?.files);
  }

  function removeSelectedFile(index) {
    setSelectedFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
  }

  function clearSelectedFiles() {
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploadState({ status: 'idle', message: '' });
  }

  async function submitUpload(event) {
    event.preventDefault();

    const cleanName = guestName.trim();
    if (!cleanName) {
      setUploadState({ status: 'error', message: 'Please enter your full name.' });
      return;
    }

    if (selectedFiles.length === 0) {
      setUploadState({ status: 'error', message: 'Please choose at least one photo.' });
      return;
    }

    const payload = new FormData();
    payload.append('guestName', cleanName);
    selectedFiles.forEach((file) => payload.append('photos', file));

    setUploadState({ status: 'submitting', message: 'Uploading photos...' });

    try {
      const response = await fetch(
        getApiUrl(`/photo-albums/${encodeURIComponent(albumId)}/uploads`),
        { method: 'POST', body: payload }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.ok === false) {
        throw new Error(data.message || 'Photo upload failed.');
      }

      setGuestName('');
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadState({
        status: 'success',
        message: `Uploaded ${data.submission?.photoCount || selectedFiles.length} photo${selectedFiles.length === 1 ? '' : 's'}. Thank you.`,
      });
    } catch (err) {
      setUploadState({
        status: 'error',
        message: getFetchErrorMessage(err, 'Photo upload failed. Please try again.'),
      });
    }
  }

  return (
    <section className="epa-panel epa-panel-upload" aria-label="Photo upload form">
      <div className="epa-panel-head">
        <h1>Upload Photos</h1>
      </div>

      <form className="epa-upload-form" onSubmit={submitUpload} ref={formRef}>
        <label className="epa-field">
          <span>Full Name</span>
          <input
            type="text"
            value={guestName}
            onChange={(event) => setGuestName(event.target.value)}
            maxLength={120}
            autoComplete="name"
            placeholder="Your full name"
          />
        </label>

        <label
          className={`epa-upload-drop${isDragging ? ' epa-upload-drop-active' : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif"
            multiple
            onChange={handleFileChange}
          />
          <FaCloudUploadAlt aria-hidden="true" />
          <span>{selectedFiles.length ? `${selectedFiles.length} photo${selectedFiles.length === 1 ? '' : 's'} selected` : 'Choose or drag photos here'}</span>
          <em>JPG, PNG, WEBP, GIF, or HEIC</em>
        </label>

        {filePreviews.length > 0 && (
          <div className="epa-preview-wrap">
            <div className="epa-preview-strip" aria-label="Selected photo preview">
              {filePreviews.map((preview, index) => (
                <div className="epa-preview-item" key={preview.url}>
                  <img src={preview.url} alt={preview.name} />
                  <button
                    type="button"
                    className="epa-preview-remove"
                    onClick={() => removeSelectedFile(index)}
                    aria-label={`Remove ${preview.name}`}
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="epa-clear-files" onClick={clearSelectedFiles}>
              <FaTimes aria-hidden="true" /> Clear all
            </button>
          </div>
        )}

        <button className="epa-submit-button" type="submit" disabled={uploadState.status === 'submitting'}>
          {uploadState.status === 'submitting' ? <FaUpload aria-hidden="true" /> : <FaCheck aria-hidden="true" />}
          Submit
        </button>

        {uploadState.message && (
          <p className={`epa-form-message epa-form-message-${uploadState.status}`}>
            {uploadState.message}
          </p>
        )}

      </form>
    </section>
  );
}

function GalleryPage({ albumId }) {
  const galleryListRef = useSuppressBookGesture();
  const [galleryState, setGalleryState] = useState({
    status: 'loading',
    message: '',
    albumId,
    albumTitle: 'Public Gallery',
    photos: [],
  });
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const loadPublicGallery = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setGalleryState((current) => ({ ...current, status: 'loading', message: '' }));
    }

    try {
      const response = await fetch(
        getApiUrl(`/photo-albums/${encodeURIComponent(albumId)}/photos`),
        { headers: { Accept: 'application/json' } }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || 'Unable to load public photos.');
      }

      const photos = Array.isArray(data.photos)
        ? data.photos.filter((photo) => photo.previewFilename || photo.filename)
        : [];

      setGalleryState({
        status: 'ready',
        message: '',
        albumId: data.albumId || albumId,
        albumTitle: data.title || 'Public Gallery',
        photos,
      });
    } catch (err) {
      setGalleryState((current) => ({
        ...current,
        status: 'error',
        message: getFetchErrorMessage(err, 'Unable to load public photos.'),
      }));
    }
  }, [albumId]);

  useEffect(() => {
    loadPublicGallery();

    const refresh = () => loadPublicGallery({ silent: true });
    const intervalId = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    window.addEventListener('photo-album-uploaded', refresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('photo-album-uploaded', refresh);
    };
  }, [loadPublicGallery]);

  const photos = galleryState.photos;
  const activePhoto = lightboxIndex === null ? null : photos[lightboxIndex];

  const photoUrl = useCallback((photo) => {
    const filename = photo?.previewFilename || photo?.filename || '';
    if (!filename) return '';
    return getApiUrl(
      `/photo-albums/${encodeURIComponent(galleryState.albumId || albumId)}/files/${encodeURIComponent(filename)}`
    );
  }, [albumId, galleryState.albumId]);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrevious = useCallback(() => {
    setLightboxIndex((current) => (
      current === null ? current : (current - 1 + photos.length) % photos.length
    ));
  }, [photos.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((current) => (
      current === null ? current : (current + 1) % photos.length
    ));
  }, [photos.length]);

  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const previousOverflow = document.body.style.overflow;

    function handleKeyDown(event) {
      if (event.key === 'Escape') closeLightbox();
      else if (event.key === 'ArrowLeft') showPrevious();
      else if (event.key === 'ArrowRight') showNext();
    }

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxIndex, closeLightbox, showPrevious, showNext]);

  return (
    <section className="epa-panel epa-panel-gallery epa-native-public-gallery" aria-label="Public photo gallery">
      <div className="epa-public-gallery-root">
        <header className="epa-public-gallery-header">
          <span className="epa-public-gallery-eyebrow">Public</span>
          <h1>Gallery</h1>
          <p className="epa-public-gallery-event">{galleryState.albumTitle}</p>
          <span className="epa-public-gallery-count">
            {photos.length} public photo{photos.length === 1 ? '' : 's'}
          </span>
        </header>

        {galleryState.status === 'loading' && (
          <div className="epa-public-gallery-state">
            <span className="epa-public-gallery-spinner" aria-hidden="true" />
            <p>Loading public photos…</p>
          </div>
        )}

        {galleryState.status === 'error' && (
          <div className="epa-public-gallery-state epa-public-gallery-state-error">
            <strong>Could not load photos</strong>
            <p>{galleryState.message}</p>
            <button className="epa-public-gallery-retry" type="button" onClick={() => loadPublicGallery()}>
              Try Again
            </button>
          </div>
        )}

        {galleryState.status === 'ready' && photos.length === 0 && (
          <div className="epa-public-gallery-state">
            <span className="epa-public-gallery-empty-icon" aria-hidden="true">📷</span>
            <strong>No public photos yet</strong>
            <p>Public event photos will appear here after the organizer makes them visible.</p>
          </div>
        )}

        {galleryState.status === 'ready' && photos.length > 0 && (
          <div className="epa-public-photo-grid" ref={galleryListRef} aria-label="Public event photos">
            {photos.map((photo, index) => (
              <button
                type="button"
                className="epa-public-photo-button"
                key={photo.id || photo.previewFilename || photo.filename}
                onMouseDown={stopInteractiveEvent}
                onTouchStart={stopInteractiveEvent}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setLightboxIndex(index);
                }}
                aria-label={`View public photo ${index + 1} of ${photos.length}`}
              >
                <img
                  className="epa-public-photo-image"
                  src={photoUrl(photo)}
                  alt={`Public event photo ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                />
              </button>
            ))}
          </div>
        )}

        <p className="epa-public-gallery-readonly">Read-only public gallery · Tap a photo to view</p>
      </div>

      {activePhoto && typeof document !== 'undefined' && createPortal(
        <div
          className="epa-public-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Public event photo viewer"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeLightbox();
          }}
        >
          <button type="button" className="epa-public-lightbox-close" onClick={closeLightbox} aria-label="Close photo viewer">
            ×
          </button>
          <button
            type="button"
            className="epa-public-lightbox-nav epa-public-lightbox-prev"
            onClick={showPrevious}
            disabled={photos.length <= 1}
            aria-label="Previous photo"
          >
            ‹
          </button>
          <figure className="epa-public-lightbox-figure">
            <img
              className="epa-public-lightbox-image"
              src={photoUrl(activePhoto)}
              alt={`Public event photo ${lightboxIndex + 1} of ${photos.length}`}
            />
            <figcaption className="epa-public-lightbox-caption">
              {lightboxIndex + 1} / {photos.length}
            </figcaption>
          </figure>
          <button
            type="button"
            className="epa-public-lightbox-nav epa-public-lightbox-next"
            onClick={showNext}
            disabled={photos.length <= 1}
            aria-label="Next photo"
          >
            ›
          </button>
        </div>,
        document.body
      )}
    </section>
  );
}

export default function MeriPhotoAlbum() {
  const { albumId: routeAlbumId } = useParams();
  const albumId = normalizeAlbumId(routeAlbumId);
  const videoItems = useMemo(() => getVideoItems(), []);
  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const [activePage, setActivePage] = useState(0);
  const bookRef = useRef(null);
  const [albumUrl] = useState(() => {
    if (typeof window === 'undefined') return CLEAN_ALBUM_URL;
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    return url.toString();
  });

  const computeBookSize = useCallback(() => {
    if (typeof window === 'undefined') return { width: 600, height: 920 };
    const width = Math.round(Math.min(600, window.innerWidth * 0.9));
    const height = Math.round(Math.max(440, Math.min(920, window.innerHeight * 0.87)));
    return { width, height };
  }, []);

  const [bookSize, setBookSize] = useState(computeBookSize);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Mobile browsers fire a native 'resize' event whenever their address
    // bar/toolbar shows or hides — which happens on ordinary taps, not just
    // scrolling — changing window.innerHeight with no real layout change.
    // react-pageflip's "stretch" size mode re-measures its container on every
    // 'resize' it sees, so reacting to those made the whole book visibly jump
    // on every "Next" tap. Recomputing only when the WIDTH actually changes
    // (a real rotation or window resize, not a toolbar) and feeding that as a
    // fixed inline size keeps the book's measured container stable so the
    // library's own re-measurement finds nothing to react to.
    let lastWidth = window.innerWidth;

    function handleResize() {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      setBookSize(computeBookSize());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [computeBookSize]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // react-pageflip's "stretch" size mode measures its parent on mount, before
    // the surrounding CSS grid has finished laying out, and never re-measures on
    // its own — force a resize event so it recalculates against the real size.
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  // The in-page CTAs (monogram, "Upload Photos") sit inside the book's own
  // DOM, so react-pageflip's animated flip()/flipNext()/flipPrev() — which
  // drive a coordinate-based gesture calculator shared with real corner
  // drags — either double-advance (racing the library's own native
  // click-to-flip) or leave that gesture calculator in a state that blocks
  // clicks on the next page. turnToPage() is a direct, non-gesture page
  // switch with neither failure mode, so these two shortcuts use it and
  // sync activePage themselves instead of trusting onFlip.
  const openVideoPage = useCallback(() => {
    bookRef.current?.pageFlip().turnToPage(1);
    setActivePage(1);
  }, []);

  const openUploadPage = useCallback(() => {
    bookRef.current?.pageFlip().turnToPage(2);
    setActivePage(2);
  }, []);

  const handleFlip = useCallback((event) => {
    setActivePage(event.data);
  }, []);

  // Stable page elements: none of these depend on state that changes during
  // normal use, so this array's identity never changes after mount, and
  // HTMLFlipBook never tears down/rebuilds the pages while a guest is typing.
  const pageContent = useMemo(() => [
    <IntroPage albumUrl={albumUrl} onOpenVideo={openVideoPage} />,
    <VideoPage videoItems={videoItems} onUploadClick={openUploadPage} />,
    <UploadPage albumId={albumId} />,
    <GalleryPage albumId={albumId} />,
  ], [albumUrl, videoItems, albumId, openVideoPage, openUploadPage]);
  const totalBookPages = pageContent.length;

  const turnToBookPage = useCallback((pageIndex) => {
    const pageFlip = bookRef.current?.pageFlip();
    if (!pageFlip) return;

    const pageCount = pageFlip.getPageCount?.() || totalBookPages;
    const targetPage = Math.min(Math.max(pageIndex, 0), pageCount - 1);
    if (targetPage === activePage) return;

    pageFlip.turnToPage(targetPage);
    setActivePage(targetPage);
  }, [activePage, totalBookPages]);

  const flipToNext = useCallback(() => {
    turnToBookPage(activePage + 1);
  }, [activePage, turnToBookPage]);

  const flipToPrev = useCallback(() => {
    turnToBookPage(activePage - 1);
  }, [activePage, turnToBookPage]);

  return (
    <main
      className="epa-page-shell"
      style={{
        '--epa-intro-desktop': `url(${introDesktop})`,
        '--epa-intro-mobile': `url(${introMobile})`,
        '--epa-book-bg': `url(${bookPageBg})`,
        '--epa-paper': `url(${paperTexture})`,
      }}
    >
      <div className="epa-bg-ambient" aria-hidden="true">
        <span className="epa-bg-orb epa-bg-orb-1" />
        <span className="epa-bg-orb epa-bg-orb-2" />
        <span className="epa-bg-orb epa-bg-orb-3" />
        <span className="epa-bg-orb epa-bg-orb-4" />
        <span className="epa-bg-spark epa-bg-spark-1" />
        <span className="epa-bg-spark epa-bg-spark-2" />
        <span className="epa-bg-spark epa-bg-spark-3" />
        <span className="epa-bg-spark epa-bg-spark-4" />
        <span className="epa-bg-spark epa-bg-spark-5" />
        <span className="epa-bg-spark epa-bg-spark-6" />
      </div>

      <div className="epa-stage" aria-live="polite">
        <HTMLFlipBook
          ref={bookRef}
          width={bookSize.width}
          height={bookSize.height}
          size="stretch"
          minWidth={320}
          maxWidth={bookSize.width}
          minHeight={440}
          maxHeight={bookSize.height}
          drawShadow={!prefersReducedMotion}
          flippingTime={prefersReducedMotion ? 1 : 900}
          usePortrait
          startPage={0}
          showCover={false}
          maxShadowOpacity={0.16}
          autoSize={false}
          mobileScrollSupport={false}
          clickEventForward
          disableFlipByClick
          showPageCorners={false}
          className="epa-flipbook"
          style={{ width: bookSize.width, height: bookSize.height }}
          onFlip={handleFlip}
        >
          {pageContent.map((content, index) => (
            <BookPage key={index}>
              {content}
            </BookPage>
          ))}
        </HTMLFlipBook>
      </div>

      <div className="epa-bottom-nav-wrap">
        <div className="epa-bottom-nav">
          <button type="button" onClick={flipToPrev} disabled={activePage === 0} aria-label="Previous page">
            <FaArrowLeft aria-hidden="true" />
          </button>
          <span>{activePage + 1} / {totalBookPages}</span>
          <button type="button" onClick={flipToNext} disabled={activePage === totalBookPages - 1} aria-label="Next page">
            <FaArrowRight aria-hidden="true" />
          </button>
        </div>
      </div>
    </main>
  );
}
